import { ShieldCheck, CalendarClock, Boxes, Mountain } from "lucide-react";
import { MEDIA } from "@/lib/constants";

const ITEMS = [
    {
        icon: ShieldCheck,
        title: "Fully Insured",
        body: "$2M general liability + active workers' comp. COIs on file within 24 hours of award.",
    },
    {
        icon: CalendarClock,
        title: "Built for Schedule",
        body: "Weekly look-aheads, two-week scheduling commitments, and direct PM-to-PM communication.",
    },
    {
        icon: Boxes,
        title: "Single-Source Trades",
        body: "Turf, hardscape, fencing, and irrigation crews under one PO. Fewer subs, fewer surprises.",
    },
    {
        icon: Mountain,
        title: "Wasatch Front Native",
        body: "Salt Lake, Utah, Davis, Weber, and Summit counties. We understand local soils, code, and freeze-thaw.",
    },
];

export const Partner = () => {
    return (
        <section
            id="partner"
            data-testid="partner-section"
            className="relative bg-bone py-24 md:py-32 border-t border-line"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    {/* Left: image + heading */}
                    <div className="lg:col-span-5 reveal">
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-5">
                            <span className="inline-block w-8 h-px bg-terracotta align-middle mr-3" />
                            Why Partner
                        </div>
                        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter font-medium text-ink leading-[1.02]">
                            Built like a trade. <br />
                            Operated like a partner.
                        </h2>
                        <p className="mt-6 text-base md:text-lg text-stone leading-relaxed max-w-md">
                            We don't sell lawns to homeowners. We deliver scopes for builders, developers,
                            and civil contractors who need a landscape sub that shows up, hits dates,
                            and closes punch lists.
                        </p>
                        <div className="mt-10 relative overflow-hidden border border-line">
                            <img
                                src={MEDIA.whyPartner}
                                alt="Construction site partnership"
                                className="w-full h-80 object-cover grayscale-[15%] hover:grayscale-0 transition-all duration-700"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-ink/85 backdrop-blur-sm text-bone px-6 py-4">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-bone/60 font-semibold mb-1">Current Capacity</div>
                                <div className="font-heading text-xl">Accepting Q1 & Q2 awards</div>
                            </div>
                        </div>
                    </div>

                    {/* Right: technical grid of value props */}
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-1 md:grid-cols-2 border-l border-t border-line">
                            {ITEMS.map((it, i) => (
                                <div
                                    key={it.title}
                                    data-testid={`value-prop-${i}`}
                                    className="reveal group p-8 md:p-10 border-r border-b border-line bg-bone hover:bg-sand transition-colors duration-500"
                                    style={{ transitionDelay: `${i * 60}ms` }}
                                >
                                    <div className="flex items-center justify-between mb-8">
                                        <it.icon size={28} strokeWidth={1.5} className="text-sage" />
                                        <span className="font-heading text-xs text-stone tabular-nums">
                                            0{i + 1} / 0{ITEMS.length}
                                        </span>
                                    </div>
                                    <h3 className="font-heading text-2xl tracking-tight font-medium text-ink mb-3">
                                        {it.title}
                                    </h3>
                                    <p className="text-sm md:text-base text-stone leading-relaxed">
                                        {it.body}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Credentials strip */}
                        <div className="mt-8 grid grid-cols-3 gap-4 text-center border border-line bg-sand">
                            <div className="py-5 border-r border-line">
                                <div className="font-heading text-2xl md:text-3xl text-sage font-medium">UT Lic.</div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-stone mt-1">DOPL Verified</div>
                            </div>
                            <div className="py-5 border-r border-line">
                                <div className="font-heading text-2xl md:text-3xl text-sage font-medium">EPA</div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-stone mt-1">WaterSense</div>
                            </div>
                            <div className="py-5">
                                <div className="font-heading text-2xl md:text-3xl text-sage font-medium">UAA</div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-stone mt-1">Member</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
